import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Topbar from "@/components/layout/Topbar";
import { api, formatMoney } from "@/lib/api";
import { toast, Toaster } from "sonner";

const TYPES = ["", "BUDGET", "SPEND", "REVENUE", "FINE"];

export default function RecordsPage() {
  const [searchParams] = useSearchParams();
  const initialType = (searchParams.get("type") || "").toUpperCase();
  const initialSearch = searchParams.get("search") || "";
  const initialCompany = searchParams.get("company_id") || "";

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    company_id: initialCompany,
    record_type: TYPES.includes(initialType) ? initialType : "",
    erp: "",
    search: initialSearch,
  });
  const [page, setPage] = useState(0);
  const limit = 50;
  const [companies, setCompanies] = useState([]);
  const [erps, setErps] = useState([]);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/meta/erp-systems").then((r) => setErps(r.data.systems));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { ...filters, skip: page * limit, limit };
    Object.keys(params).forEach((k) => params[k] === "" && delete params[k]);
    api
      .get("/records", { params })
      .then((r) => {
        setRecords(r.data.items);
        setTotal(r.data.total);
      })
      .catch(() => toast.error("Failed to load records"))
      .finally(() => setLoading(false));
  }, [filters, page]);

  const pages = Math.ceil(total / limit);
  const setFilter = (k, v) => {
    setPage(0);
    setFilters((f) => ({ ...f, [k]: v }));
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Records"
        subtitle={`${total.toLocaleString()} unified records across ERPs`}
      />
      <div className="space-y-4 p-6 animate-fade-in" data-testid="records-page">
        <div className="grid grid-cols-2 gap-3 rounded-md border border-slate-200 bg-white p-4 md:grid-cols-4">
          <Select label="Company" value={filters.company_id} onChange={(v) => setFilter("company_id", v)} testId="filter-company">
            <option value="">All companies</option>
            {companies.map((c) => (
              <option key={c.company_id} value={c.company_id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select label="Type" value={filters.record_type} onChange={(v) => setFilter("record_type", v)} testId="filter-type">
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t || "All types"}
              </option>
            ))}
          </Select>
          <Select label="ERP" value={filters.erp} onChange={(v) => setFilter("erp", v)} testId="filter-erp">
            <option value="">All ERPs</option>
            {erps.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </Select>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Search
            </label>
            <input
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
              data-testid="filter-search"
              placeholder="Category, dept, description…"
              className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Company</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">ERP</th>
                  <th className="px-4 py-3 text-left">Description</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && records.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                      No records match your filters.
                    </td>
                  </tr>
                )}
                {records.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-500 tabular">
                      {new Date(r.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-medium text-navy-600">
                      {r.companyName}
                    </td>
                    <td className="px-4 py-2.5">
                      <TypeBadge type={r.recordType} />
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{r.category || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">{r.sourceERP}</td>
                    <td className="max-w-[300px] truncate px-4 py-2.5 text-slate-500">
                      {r.description || "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium tabular text-navy-600">
                      {formatMoney(r.amount, r.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
            <div>
              Showing {records.length} of {total.toLocaleString()}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-sm border border-slate-200 px-2 py-1 text-slate-600 hover:border-navy-600 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="tabular">
                {page + 1} / {Math.max(pages, 1)}
              </span>
              <button
                type="button"
                disabled={page + 1 >= pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-sm border border-slate-200 px-2 py-1 text-slate-600 hover:border-navy-600 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Select({ label, value, onChange, children, testId }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
      >
        {children}
      </select>
    </div>
  );
}

function TypeBadge({ type }) {
  const map = {
    REVENUE: "bg-emerald-100 text-emerald-700",
    SPEND: "bg-orange-100 text-brand-orange",
    BUDGET: "bg-navy-600/10 text-navy-600",
    FINE: "bg-red-100 text-red-700",
  };
  return (
    <span
      className={`rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
        map[type] || "bg-slate-100 text-slate-600"
      }`}
    >
      {type}
    </span>
  );
}
